import React from 'react'
import { ConfigurationContext } from './configuration-context'
import { getClassNames } from '../../../_utils/classnames'
import { Well } from '@pushly/aqe/lib/components'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Skeleton, Switch, Popover } from 'antd'
import { extractJourneyTrigger } from '../../../_utils/journeys'
import moment, { Moment } from 'moment'
import { BetterTimePicker } from '@pushly/aqe/lib/components/better-timepicker/better-timepicker'
import { TriggerType } from '@pushly/models/lib/enums/journeys'

interface ISendSettingsWell {
    submitConfiguration?: Record<string, unknown>
}

interface IState {}

export class SendSettingsWell extends React.Component<ISendSettingsWell, IState> {
    public static contextType = ConfigurationContext
    public context!: React.ContextType<typeof ConfigurationContext>

    public state: IState = {}

    public render() {
        const { mode, loading, journey, flags } = this.context

        let quietHoursStart
        if (journey.configuration.quiet_hours?.start) {
            quietHoursStart = moment(journey.configuration.quiet_hours.start, 'hh:mm A')
        }

        let quietHoursEnd
        if (journey.configuration.quiet_hours?.end) {
            quietHoursEnd = moment(journey.configuration.quiet_hours.end, 'hh:mm A')
        }

        const trigger = extractJourneyTrigger(journey)
        const triggerConfig = trigger?.configuration
        const triggerType = triggerConfig?.type
        const isSubscribedTrigger = triggerType === TriggerType.SUBSCRIBED

        return (
            <Well
                className={getClassNames(null, 'journey-send-settings-well', 'nested', `mode-${mode}`, {
                    ['final-well']: !!this.props.submitConfiguration,
                })}
                title="Send Settings"
                hideFooter={!this.props.submitConfiguration}
                {...this.props.submitConfiguration}
            >
                <Skeleton
                    loading={loading}
                    active={true}
                    title={false}
                    paragraph={{
                        rows: 1,
                        width: '100%',
                    }}
                >
                    {!isSubscribedTrigger && (
                        <Well
                            mode="ghost"
                            title={
                                <>
                                    <span>Quiet Hours</span>
                                    <Popover
                                        overlayClassName="quiet-hours-overlay"
                                        content={
                                            <p>
                                                Enabling Quiet Hours delays notifications from being sent during the
                                                chosen timeframe in the subscriber’s time zone. Any notifications that
                                                would have been sent during this time will be sent in the first hour
                                                after Quiet Hours ends.
                                            </p>
                                        }
                                    >
                                        <QuestionCircleOutlined className="info-icon" />
                                    </Popover>
                                </>
                            }
                            hideFooter={true}
                        >
                            <div className="switch-row">
                                <div className="switch-left">
                                    <Switch
                                        size="small"
                                        checked={flags.quietHoursEnabled ?? false}
                                        onChange={this.handleQuietHoursEnabledChange}
                                    />
                                </div>
                                <div className="switch-right">
                                    <span>Enable Quiet Hours</span>
                                </div>
                            </div>

                            {flags.quietHoursEnabled && (
                                <div className="quiet-hours-wrapper">
                                    <span>Do not send notifications between</span>
                                    <BetterTimePicker
                                        className="quiet-hours-start-time"
                                        size="small"
                                        placeholder="Start time"
                                        use12Hours={true}
                                        format="hh:mm A"
                                        value={quietHoursStart}
                                        onTimeChange={this.handleQuietHoursStartChange}
                                    />
                                    <span> and </span>
                                    <BetterTimePicker
                                        className="quiet-hours-end-time"
                                        size="small"
                                        placeholder="End time"
                                        use12Hours={true}
                                        format="hh:mm A"
                                        value={quietHoursEnd}
                                        onTimeChange={this.handleQuietHoursEndChange}
                                    />
                                    <span> in subscriber’s time zone.</span>
                                </div>
                            )}
                        </Well>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected handleQuietHoursEnabledChange = (quietHoursEnabled: boolean) => {
        const updated = structuredClone(this.context.journey)

        updated.configuration.quiet_hours = undefined

        this.context.setJourney(updated)
        this.context.setFlags({ quietHoursEnabled })
    }

    protected handleQuietHoursStartChange = (start: Moment | null) => {
        const updated = structuredClone(this.context.journey)

        updated.configuration.quiet_hours = {
            start: start?.format('HH:mm') ?? '',
            end: updated.configuration.quiet_hours?.end ?? '',
        }

        this.context.setJourney(updated)
    }

    protected handleQuietHoursEndChange = (end: Moment | null) => {
        const updated = structuredClone(this.context.journey)

        updated.configuration.quiet_hours = {
            start: updated.configuration.quiet_hours?.start ?? '',
            end: end?.format('HH:mm') ?? '',
        }

        this.context.setJourney(updated)
    }
}
